import { ColDef, ICellRendererParams, themeAlpine } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import startCase from "lodash.startcase";
import { Copy, Pencil, X } from "lucide-react";
import React, { useMemo } from "react";
import { toast } from "react-toastify";

import { IgSwitch, IgTableWrapper, IgTypography } from "@intelligems/ig-theme";
import { WebhookDto } from "@intelligems/ig-types";
import { Box, Grid, IconButton, useTheme } from "@mui/material";
import { AreYouSure } from "@src/components/Popover/AreYouSure";
import { copyToClipboard } from "@src/utils/copyToClipboard";

interface WebhookTableProps {
  webhooks: WebhookDto[];
  isLoading: boolean;
  onEdit: (webhook: WebhookDto) => void;
  onDelete: (webhookId: string) => void;
  onToggleEnabled: (webhook: WebhookDto) => void;
  editingId: string | null;
  deleteWebhook: { isPending: boolean };
  updateWebhook: any;
}

const StatusCellRenderer = (params: ICellRendererParams<WebhookDto>) => {
  const { onToggleEnabled } = params.context;

  if (!params.data) return null;

  const handleToggle = () => {
    onToggleEnabled(params.data!);
  };

  return (
    <Box sx={{ marginLeft: "20px" }}>
      <IgSwitch
        checked={params.data.enabled}
        onChange={handleToggle}
        label={params.data.enabled ? "Enabled" : "Disabled"}
      />
    </Box>
  );
};

const ActionsCellRenderer = (params: ICellRendererParams<WebhookDto>) => {
  const { onEdit, onDelete, editingId, deleteWebhook } = params.context;
  const theme = useTheme();

  if (!params.data) return null;

  const handleDeleteClick = () => {
    onDelete(params.data!.id);
  };

  return (
    <Grid container spacing={1}>
      <Grid item>
        <IconButton
          size="small"
          onClick={() => onEdit(params.data!)}
          disabled={editingId === params.data.id}
        >
          <Pencil size={18} />
        </IconButton>
      </Grid>
      <Grid item>
        <AreYouSure
          enabled={true}
          keyId={`delete-webhook-${params.data.id}`}
          isLoading={deleteWebhook.isPending}
          onYesClick={handleDeleteClick}
          message="Are you sure you want to delete this webhook?"
          yesMessage="Yes, delete"
        >
          <IconButton
            size="small"
            disabled={deleteWebhook.isPending}
            color="error"
          >
            <X size={18} color={theme.igIconColors.error} />
          </IconButton>
        </AreYouSure>
      </Grid>
    </Grid>
  );
};

const ActionTypeCellRenderer = (params: ICellRendererParams<WebhookDto>) => {
  const actionTypes = params.value as string[];

  if (!actionTypes || actionTypes.length === 0) {
    return (
      <IgTypography variant="body2" color="text.secondary">
        None
      </IgTypography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
      {actionTypes.map((actionType, index) => (
        <IgTypography
          key={actionType}
          variant="body2"
          sx={{
            backgroundColor: "action.hover",
            padding: "2px 6px",
            borderRadius: "4px",
            fontSize: "12px",
          }}
        >
          {startCase(actionType)}
          {index < actionTypes.length - 1 ? "" : ""}
        </IgTypography>
      ))}
    </Box>
  );
};

const UrlCellRenderer = (params: ICellRendererParams<WebhookDto>) => {
  return (
    <IgTypography variant="body2" sx={{ wordBreak: "break-all" }}>
      {params.value}
    </IgTypography>
  );
};

const SecretCellRenderer = (params: ICellRendererParams<WebhookDto>) => {
  const secretValue = params.value || "";
  const maskedSecret = secretValue ? "•".repeat(8) + secretValue.slice(-4) : "";

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <IgTypography
        variant="body2"
        sx={{ fontFamily: "monospace", minWidth: "120px" }}
      >
        {maskedSecret}
      </IgTypography>
      {secretValue && (
        <IconButton
          size="small"
          onClick={() => {
            void copyToClipboard(secretValue);
            toast.success("Secret Copied");
          }}
          sx={{ padding: 0.5 }}
          title="Copy secret to clipboard"
        >
          <Copy size={14} />
        </IconButton>
      )}
    </Box>
  );
};

const NoRowsOverlay = () => (
  <div style={{ padding: "20px", textAlign: "center" }}>
    <IgTypography color="text.secondary">
      No webhooks configured. Create your first webhook above.
    </IgTypography>
  </div>
);

export const WebhookTable: React.FC<WebhookTableProps> = ({
  webhooks,
  isLoading,
  onEdit,
  onDelete,
  onToggleEnabled,
  editingId,
  deleteWebhook,
  updateWebhook,
}) => {
  const theme = useTheme();

  // Configure AgGrid theme
  const gridTheme = useMemo(() => {
    return themeAlpine.withParams({
      fontFamily: theme.typography.fontFamily,
      fontSize: 14,
    });
  }, [theme]);

  // Column definitions
  const columnDefs = useMemo<ColDef<WebhookDto>[]>(() => {
    const defaultColDef: ColDef<WebhookDto> = {
      resizable: false,
      suppressHeaderMenuButton: true,
      suppressHeaderContextMenu: true,
      suppressAutoSize: true,
    };

    return [
      {
        ...defaultColDef,
        headerName: "URL",
        field: "url",
        flex: 1,
        minWidth: 200,
        cellRenderer: UrlCellRenderer,
      },
      {
        ...defaultColDef,
        headerName: "Action Types",
        field: "actionTypes",
        width: 200,
        cellRenderer: ActionTypeCellRenderer,
      },
      {
        ...defaultColDef,
        headerName: "Secret",
        field: "secret",
        width: 200,
        cellRenderer: SecretCellRenderer,
      },
      {
        ...defaultColDef,
        headerName: "Status",
        field: "enabled",
        width: 180,
        cellRenderer: StatusCellRenderer,
      },
      {
        ...defaultColDef,
        headerName: "Actions",
        width: 120,
        cellRenderer: ActionsCellRenderer,
        sortable: false,
        filter: false,
      },
    ];
  }, []);

  // Grid context for cell renderers
  const gridContext = useMemo(
    () => ({
      onEdit,
      onDelete,
      onToggleEnabled,
      editingId,
      deleteWebhook,
      updateWebhook,
    }),
    [onEdit, onDelete, onToggleEnabled, editingId, deleteWebhook, updateWebhook]
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <IgTypography variant={"h3"} sx={{ fontWeight: 600 }}>
          Configured Webhooks
        </IgTypography>
      </Grid>
      <Grid
        item
        sx={{
          width: "100%",
          "& .ag-center-cols-clipper": {
            minHeight: "50px !important",
          },
        }}
      >
        <IgTableWrapper height={null} minHeight={"100px"}>
          <AgGridReact<WebhookDto>
            theme={gridTheme}
            rowData={webhooks}
            columnDefs={columnDefs}
            context={gridContext}
            loading={isLoading}
            headerHeight={35}
            domLayout="autoHeight"
            suppressCellFocus={true}
            enableCellTextSelection={true}
            suppressMovableColumns={true}
            suppressContextMenu={true}
            animateRows={false}
            rowHeight={56}
            noRowsOverlayComponent={NoRowsOverlay}
          />
        </IgTableWrapper>
      </Grid>
    </Grid>
  );
};
